// One notification bell in every Dugzi app (D-110, suite-integration §4.3; DZ-205): the unread count
// across products, live (Server-Sent Events through this app's backend, `/api/suite/...`), and a panel
// that loads on first click (D-102), so the bell adds little to an app's first visit.
import { useEffect, useId, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { Tooltip } from './tooltip';
import { setRailBadges } from './apprail';

export interface NotificationBellProps {
  /** The account this tab uses (D-028): sent as `X-Dugzi-Account`. */
  account?: string;
  /** Dugzi Account's public URL (links into other apps keep the account). */
  accountsUrl: string;
  testId?: string;
}

/** Headers for this tab's account. */
export function suiteHeaders(account?: string, json = false): Record<string, string> {
  const h: Record<string, string> = {};
  if (account) h['X-Dugzi-Account'] = account;
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

/** Follows the unread count: first from `/unread`, then live; reconnects after a pause when cut. */
function useUnread(account?: string): [number, (n: number) => void] {
  const [n, setN] = useState(0);
  useEffect(() => {
    let stop = false;
    let ctl: AbortController | null = null;
    let wait = 2000;
    const run = async () => {
      while (!stop) {
        ctl = new AbortController();
        try {
          const r = await fetch('/api/suite/notifications/stream', { headers: suiteHeaders(account), credentials: 'same-origin', signal: ctl.signal });
          // Signed out (401), a deployment without notifications (204, dz-suite's "don't reconnect"), or an app
          // without the suite routes (404).
          if (r.status === 401 || r.status === 204 || r.status === 404) return;
          if (!r.ok || !r.body) throw new Error(String(r.status));
          wait = 2000;
          const reader = r.body.pipeThrough(new TextDecoderStream()).getReader();
          let buf = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += value;
            let i: number;
            while ((i = buf.indexOf('\n\n')) >= 0) {
              const frame = buf.slice(0, i);
              buf = buf.slice(i + 2);
              const data = frame.split('\n').find((l) => l.startsWith('data:'));
              if (frame.includes('event: unread') && data) {
                try { const d = JSON.parse(data.slice(5)); setN(d.unread ?? 0); setRailBadges(d.by_product); } catch { /* a partial frame */ }
              }
            }
          }
        } catch {
          if (stop) return;
        }
        await new Promise((res) => setTimeout(res, wait));
        wait = Math.min(wait * 2, 30_000);
      }
    };
    void run();
    return () => { stop = true; ctl?.abort(); };
  }, [account]);
  return [n, setN];
}

/** The bell: a button with the unread count; the panel (list, mark all read, settings) on click. */
export function NotificationBell({ account, accountsUrl, testId = 'notification-bell' }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useUnread(account);
  const [, loaded] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  const Panel = panelModule;
  useEffect(() => { if (open && !panelModule) loadPanel().then(() => loaded((n) => n + 1)).catch(() => {}); }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); ref.current?.querySelector<HTMLButtonElement>('button')?.focus(); } };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', esc, true); };
  }, [open]);
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';
  return (
    <div className="dz-bell" ref={ref}>
      <Tooltip content="Notifications" disabled={open}>
        <button type="button" className="dz-icon-btn dz-bell__btn" aria-label={label} aria-expanded={open} aria-controls={open && Panel ? id : undefined}
          onClick={() => setOpen((o) => !o)} data-testid={testId}>
          <Bell aria-hidden />
          {unread > 0 && <span className="dz-bell__badge" data-testid={`${testId}-count`} aria-hidden>{unread > 99 ? '99+' : unread}</span>}
        </button>
      </Tooltip>
      {open && Panel && <Panel.BellPanel id={id} account={account} accountsUrl={accountsUrl} onUnread={setUnread} onClose={() => setOpen(false)} />}
    </div>
  );
}

let panelModule: typeof import('./bell-panel') | null = null;
const loadPanel = () => import('./bell-panel').then((m) => { panelModule = m; return m; });
